import { gesamtUmfang, type GesamtUmfang } from '@awf/shared';
import { multiFactor, onIdTokenChanged, signOut } from 'firebase/auth';
import { initFirebase } from '../firebase.js';

export interface NutzerInfo {
  uid: string;
  email: string | null;
  /**
   * `displayName` des Kontos, gesetzt von `tools/invite-user.js --name`. Fehlt bei aelteren
   * Konten; die Oberflaeche faellt dann auf die Adresse zurueck.
   */
  name?: string | null;
  emailBestaetigt: boolean;
  /**
   * Wurde DIESE Sitzung mit einem zweiten Faktor angemeldet? Das ist der Anspruch, den die
   * Firestore-Rules und das Backend pruefen -- nicht dasselbe wie
   * `hatFaktorEingeschrieben`.
   */
  mitZweitemFaktor: boolean;
  /** Hat das KONTO einen zweiten Faktor? Entscheidet, ob die Einrichtung noetig ist. */
  hatFaktorEingeschrieben: boolean;
  /**
   * Welche Gesamtansicht das Konto hat, aus Claim `rolle` und Adresse (`gesamtUmfang` in
   * packages/shared, gesetzt ueber tools/rolle.js). Steuert NUR die Anzeige des Umschalters.
   * Durchgesetzt wird das Recht in den Firestore-Rules und im Backend; ein manipulierter Wert
   * hier fuehrt hoechstens zu einer abgewiesenen Abfrage.
   */
  gesamtUmfang?: GesamtUmfang | null;
}

export interface AuthQuelle {
  beobachte(hoerer: (nutzer: NutzerInfo | null) => void): () => void;
  meldeAb(): Promise<void>;
}

export function firebaseAuthQuelle(): AuthQuelle {
  return {
    beobachte(hoerer) {
      let abbestellen: (() => void) | null = null;
      let abgebaut = false;

      void initFirebase()
        .then(({ auth }) => {
          if (abgebaut) return;
          // `onIdTokenChanged`, nicht `onAuthStateChanged`: Nach `resolveSignIn` aendert sich
          // der Anspruch `sign_in_second_factor`, ohne dass sich der angemeldete Nutzer
          // aendert. `onAuthStateChanged` feuert dabei nicht.
          abbestellen = onIdTokenChanged(auth, async (nutzer) => {
            if (!nutzer) {
              hoerer(null);
              return;
            }
            const token = await nutzer.getIdTokenResult();
            const zweiter = (
              token.claims.firebase as { sign_in_second_factor?: unknown } | undefined
            )?.sign_in_second_factor;
            hoerer({
              uid: nutzer.uid,
              email: nutzer.email,
              name: nutzer.displayName,
              emailBestaetigt: nutzer.emailVerified,
              mitZweitemFaktor: typeof zweiter === 'string' && zweiter.length > 0,
              hatFaktorEingeschrieben: multiFactor(nutzer).enrolledFactors.length > 0,
              gesamtUmfang: gesamtUmfang(token.claims.rolle, nutzer.email),
            });
          });
        })
        .catch(() => {
          if (!abgebaut) hoerer(null);
        });

      return () => {
        abgebaut = true;
        abbestellen?.();
      };
    },

    async meldeAb() {
      const { auth } = await initFirebase();
      await signOut(auth);
    },
  };
}
